import "./index.css";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import type { IInputProps, ISelectOption, ISingleSelectProps } from "@/ui-kit";
import { Button, FormField, Input, Typography } from "@/ui-kit";
import { useTranslation } from "react-i18next";
import get from "lodash/get";
import { useCallback, useMemo } from "react";
import { ChevronDownIcon } from "lucide-react";
import {
  formatIncompletePhoneNumber,
  validatePhoneNumberLength,
} from "libphonenumber-js";
import { SupportedCountry } from "./types";
import { formatPhoneValue, parsePhoneValue } from "./utils";
import { SUPPORTED_COUNTRIES } from "./constants";

type IProps = IInputProps & {
  name: string;
  selectProps?: Partial<ISingleSelectProps>;
};

export const PhoneControl = (props: IProps) => {
  const { t } = useTranslation();
  const { errors } = useFormState();
  const { setValue } = useFormContext();
  const value = useWatch({ name: props.name });
  const rootError = get(errors, props.name);

  const parsedValue = useMemo(() => {
    return parsePhoneValue(value);
  }, [value]);

  const selectedCountry = useMemo(() => {
    if (parsedValue) {
      return SUPPORTED_COUNTRIES.find(
        (country) => country.code === parsedValue.country,
      );
    }
    return SUPPORTED_COUNTRIES.find(
      (country) => country.code === SupportedCountry.UnitedStates,
    );
  }, [parsedValue]);

  const phoneNumber = parsedValue?.number || "";

  // @ts-ignore
  const countryCodeOptions: ISelectOption[] = useMemo(
    () =>
      SUPPORTED_COUNTRIES.map((country) => ({
        id: country.code,
        text: country.callingCode,
        description: country.name,
        icon: (
          <img
            className="nuco-phone-control__flag"
            src={country.flag}
            alt={`${country.name} flag`}
          />
        ),
      })),
    [],
  );

  // @ts-ignore
  const onCountryCodeChange = useCallback(
    (option: string | undefined) => {
      const newCountry = SUPPORTED_COUNTRIES.find(
        (country) => country.code === option,
      );
      if (!newCountry) return;

      const newValue = formatPhoneValue(
        newCountry.code,
        newCountry.callingCode.replace("+", ""),
        phoneNumber,
      );

      setValue(props.name, newValue, { shouldDirty: true });
    },
    [phoneNumber, setValue, props.name],
  );

  const handlePhoneChange = useCallback(
    (value: string) => {
      const numericValue = value.replace(/\D/g, "");
      const currentNumericValue = phoneNumber.replace(/\D/g, "");
      const countryCode =
        selectedCountry?.code || SupportedCountry.UnitedStates;

      // Prevent users from typing 1 or 0 as the first digit for US/CA numbers
      if (
        numericValue.length === 1 &&
        (numericValue === "1" || numericValue === "0") &&
        countryCode === SupportedCountry.UnitedStates
      ) {
        return;
      }

      const lengthValidation = validatePhoneNumberLength(
        numericValue,
        countryCode,
      );
      const isValidLength = lengthValidation === undefined;
      const isTooLong = lengthValidation === "TOO_LONG";

      if (
        numericValue.length <= currentNumericValue.length ||
        (!isTooLong && (isValidLength || lengthValidation === "TOO_SHORT"))
      ) {
        const newValue = formatPhoneValue(
          countryCode,
          selectedCountry?.callingCode.replace("+", "") || "1",
          numericValue,
        );

        setValue(props.name, newValue, { shouldDirty: true });
      }
    },
    [selectedCountry, setValue, props.name, phoneNumber],
  );

  const displayValue = useMemo(() => {
    if (!phoneNumber) return "";

    const countryCode = selectedCountry?.code || SupportedCountry.UnitedStates;
    const callingCode = selectedCountry?.callingCode || "+1";

    const internationalFormatted = formatIncompletePhoneNumber(
      `${callingCode}${phoneNumber}`,
      countryCode,
    );

    return internationalFormatted.replace(
      new RegExp(`^\\${callingCode}\\s?`),
      "",
    );
  }, [phoneNumber, selectedCountry]);

  // @ts-ignore
  const onRenderAnchor = useCallback(
    (_?: boolean, triggerProps?: Record<string, any>) => {
      return (
        <Button
          {...triggerProps}
          size="sm"
          content={
            <>
              <img
                className="nuco-phone-control__flag"
                src={selectedCountry?.flag}
                alt={`${selectedCountry?.name} flag`}
              />
              <span>{selectedCountry?.callingCode}</span>
            </>
          }
          endIcon={<ChevronDownIcon />}
          color="layer2"
        />
      );
    },
    [selectedCountry],
  );

  // const countryCodeSelect = (
  //   <Select
  //     className="nuco-phone-control__select"
  //     options={countryCodeOptions}
  //     value={selectedCountry?.code}
  //     onChange={onCountryCodeChange}
  //     maxWidth={260}
  //     matchTriggerWidth={false}
  //     onRenderAnchor={onRenderAnchor}
  //   />
  // );

  // NOTE: This is a temporary solution to display the country code select
  const countryCodeSelect = (
    <div className="nuco-phone-control__cuntry-code-placeholder">
      <img
        className="nuco-phone-control__flag"
        src={selectedCountry?.flag}
        alt={`${selectedCountry?.name} flag`}
      />
      <Typography text={selectedCountry?.callingCode} />
    </div>
  );

  return (
    <FormField
      label={t("label.phone_number")}
      message={rootError?.message?.toString()}
    >
      <Input
        className="nuco-phone-control__input"
        placeholder={t("placeholder.enter_what", {
          what: t("label.phone_number").toLowerCase(),
        })}
        {...props}
        onChange={handlePhoneChange}
        value={displayValue}
        autoComplete="off"
        invalid={!!rootError}
        startAccessory={countryCodeSelect}
        inputMode="numeric"
      />
    </FormField>
  );
};
